import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Pencil, Trash2, UserPlus } from "lucide-react";

const EMPTY = {
  id: null,
  name: "",
  phone: "",
  label: "",
  receives_warn: true,
  receives_critical: true,
  receives_recovery: true,
  active: true,
};

function fmtPhone(p) {
  if (!p) return "";
  const d = p.replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p;
}

export default function Recipients() {
  const [list, setList] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/recipients");
      setList(data);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (draft) => {
    try {
      const body = {
        name: draft.name,
        phone: draft.phone,
        label: draft.label || null,
        receives_warn: draft.receives_warn,
        receives_critical: draft.receives_critical,
        receives_recovery: draft.receives_recovery,
        active: draft.active,
      };
      if (draft.id) {
        await api.patch(`/recipients/${draft.id}`, body);
        toast.success("Recipient updated");
      } else {
        await api.post("/recipients", body);
        toast.success("Recipient added");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/recipients/${r.id}`);
      toast.success(`Removed ${r.name}`);
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
            Admin
          </div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">
            Recipients
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-xl">
            Phone numbers that receive SMS when an alert fires. Phones are
            stored as 10-digit US numbers. Toggle the per-recipient channels
            (warn / critical / recovery) to control what they get pinged for.
          </p>
        </div>
        <Button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="recipients-add-button"
          className="rounded-sm bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
        >
          <UserPlus size={14} className="mr-2" /> Add recipient
        </Button>
      </div>

      {error && (
        <div data-testid="recipients-error" className="mb-6 text-sm text-red-400 border-l-2 border-red-500 pl-3">
          {error}
        </div>
      )}

      <div className="border border-zinc-800">
        <table className="w-full text-sm" data-testid="recipients-table">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-left p-3">Label</th>
              <th className="text-left p-3">Channels</th>
              <th className="text-left p-3">Active</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {list.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-zinc-500">
                  No recipients yet — alerts will fire but no SMS will be sent. Click
                  "Add recipient" to get started.
                </td>
              </tr>
            )}
            {list.map((r) => (
              <tr key={r.id} data-testid={`recipients-row-${r.id}`} className="hover:bg-zinc-900/40">
                <td className="p-3 text-zinc-200">{r.name}</td>
                <td className="p-3 font-mono text-zinc-300">{fmtPhone(r.phone)}</td>
                <td className="p-3 text-zinc-400">{r.label || "—"}</td>
                <td className="p-3 text-xs text-zinc-400">
                  {[r.receives_warn && "WARN", r.receives_critical && "CRIT", r.receives_recovery && "OK"]
                    .filter(Boolean)
                    .join(" · ") || "none"}
                </td>
                <td className="p-3">
                  {r.active ? (
                    <span className="text-[10px] uppercase tracking-widest border border-emerald-500/40 text-emerald-300 bg-emerald-500/10 px-2 py-1">
                      ACTIVE
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest border border-zinc-700 text-zinc-400 px-2 py-1">
                      OFF
                    </span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing({ ...r })}
                    data-testid={`recipients-edit-${r.id}`}
                    className="text-zinc-400 hover:text-zinc-100"
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDel(r)}
                    data-testid={`recipients-delete-${r.id}`}
                    className="text-zinc-400 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <RecipientDialog
          form={editing}
          setForm={setEditing}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
      {confirmDel && (
        <AlertDialog open={true} onOpenChange={() => setConfirmDel(null)}>
          <AlertDialogContent
            className="bg-zinc-950 border-zinc-800 text-zinc-100"
            data-testid="recipients-confirm-delete"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {confirmDel.name}?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                They will stop receiving SMS immediately. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="rounded-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border-0"
                data-testid="recipients-confirm-cancel"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => remove(confirmDel)}
                className="rounded-sm bg-red-500 hover:bg-red-400 text-zinc-950 font-bold"
                data-testid="recipients-confirm-delete-btn"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </AppShell>
  );
}

function RecipientDialog({ form, setForm, onSave, onClose }) {
  const isNew = !form.id;
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100"
        data-testid="recipients-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            {isNew ? "Add recipient" : `Edit ${form.name}`}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Recipient form with name, phone, label, and channel opt-in toggles.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Name</Label>
            <Input
              data-testid="recipients-dialog-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Phone</Label>
            <Input
              data-testid="recipients-dialog-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(555) 123-4567 or 5551234567"
              className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm"
            />
            <div className="text-[10px] text-zinc-500 mt-1">
              10-digit US, with or without country code / separators.
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Label (optional)</Label>
            <Input
              data-testid="recipients-dialog-label"
              value={form.label || ""}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Manager, Owner"
              className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-800">
            <ToggleRow
              label="Receives WARN"
              testid="recipients-dialog-warn"
              value={form.receives_warn}
              onChange={(v) => setForm({ ...form, receives_warn: v })}
            />
            <ToggleRow
              label="Receives CRITICAL"
              testid="recipients-dialog-critical"
              value={form.receives_critical}
              onChange={(v) => setForm({ ...form, receives_critical: v })}
            />
            <ToggleRow
              label="Receives RECOVERY"
              testid="recipients-dialog-recovery"
              value={form.receives_recovery}
              onChange={(v) => setForm({ ...form, receives_recovery: v })}
            />
            <ToggleRow
              label="Active"
              testid="recipients-dialog-active"
              value={form.active}
              onChange={(v) => setForm({ ...form, active: v })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="recipients-dialog-cancel"
            className="text-zinc-400"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave(form)}
            data-testid="recipients-dialog-save"
            className="rounded-sm bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 py-2 cursor-pointer">
      <span className="text-xs uppercase tracking-widest text-zinc-300">{label}</span>
      <Switch
        checked={value}
        onCheckedChange={onChange}
        data-testid={testid}
      />
    </label>
  );
}
